import useClickOutside from "@/hooks/useClickOutSide";
import React, { useState } from "react";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { Center } from "../css";
import { LightText2 } from "../ui/Text";

interface Option {
  label: string;
  value: string;
}

interface SingleSelectWithCategoryProps {
  options: { [key: string]: Option[] };
  placeholder?: string;
  onChange: (value: Option) => void;
  selectedValue: Option;
  textClassname?: string;
  className?: string;
  dropdownClassName?: string;
  listClassName?: string;
  count?: number;
}

const SingleSelectWithCategory: React.FC<SingleSelectWithCategoryProps> = ({
  options,
  placeholder,
  onChange,
  selectedValue,
  textClassname = "text-text-primary",
  className = "",
  dropdownClassName = "",
  listClassName = "",
  count,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useClickOutside(() => setIsOpen(false));
  const [search, setSearch] = useState("");

  const handleToggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelectOption = (value: Option) => {
    onChange(value);
    setSearch("");
    setIsOpen(false);
  };

  const filteredOptions = React.useMemo(() => {
    if (!search) return options;
    const searchTermLower = search.toLowerCase();
    const filtered = Object.keys(options).reduce((acc, category) => {
      const filteredCategory = options[category].filter((option) => option.label.toLowerCase().includes(searchTermLower));
      if (filteredCategory.length) acc[category] = filteredCategory;
      return acc;
    }, {} as { [key: string]: Option[] });

    return filtered;
  }, [search, options]);

  return (
    <div className={`relative ${textClassname}`} ref={dropdownRef}>
      <button
        className={`py-2 rounded-md flex items-center !font-bold leading-normal ${className}`}
        onClick={(e) => {
          e.preventDefault();
          handleToggleDropdown();
        }}
      >
        <span>{selectedValue.label || placeholder}</span>
        {count !== undefined && <span className="px-0.5 pt-0.5 text-xs leading-normal">{` (${count})`}</span>}
        <MdOutlineKeyboardArrowDown className={`${textClassname} ${isOpen ? "rotate-180" : ""}`} size={24} />
      </button>

      {isOpen && (
        <div className={`absolute mt-1 w-fit bg-white border rounded-md shadow-lg whitespace-nowrap py-2 ${dropdownClassName}`}>
          <Center className="px-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onFocus={() => setIsOpen(true)}
              placeholder={isOpen ? "Search..." : search}
              className={`px-3 py-7 mb-2 rounded-lg border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500 cursor-pointer flex justify-between items-center transition-all w-full duration-100 ease-in-out text-nowrap text-13 outline-none placeholder:text-gray-400 text-text-default`}
            />
          </Center>

          <ul className={`flex flex-col`}>
            {Object.keys(filteredOptions).map((category) => (
              <li key={category}>
                <div className="bg-text-gray2 px-3 py-2">
                  <LightText2 text={category} className="!font-bold" />
                </div>
                {filteredOptions[category].map((option) => (
                  <li
                    key={option.value}
                    className={`px-3 py-2 hover:bg-gray-100 cursor-pointer ${listClassName} ${option.value === selectedValue.value ? "font-black" : ""}`}
                    onClick={() => handleSelectOption(option)}
                  >
                    {option.label}
                  </li>
                ))}
              </li>
            ))}
            {Object.keys(filteredOptions).length === 0 && (
              <Center className="px-4 py-6">
                <LightText2 text="No options found" />
              </Center>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SingleSelectWithCategory;
